import { Button, Skeleton, TextInput } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearch } from "@tanstack/react-router";
import { ArrowRight, Folder as FolderIcon, Search, Star } from "lucide-react";
import { useState } from "react";

import {
  getListFoldersQueryKey,
  useCreateFolder,
  useListBrands,
  useListFolders,
} from "../../../api/generated/endpoints/teams/teams";
import { TransportError } from "../../../api/errors";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { FolderNameField } from "../../../platform/folders/FolderTreeList";
import { toUserMessage } from "../../../platform/messages";
import { EmptyState } from "../../../ui/EmptyState";
import { countOf } from "../../../ui/format";
import page from "../actors/actors.module.css";
import create from "../create/create.module.css";
import gen from "../generations/generations.module.css";
import home from "../pro-home.module.css";
import { SoonControl } from "../Soon";
import classes from "./library.module.css";
import { PromptTemplatesTab } from "./PromptTemplatesTab";
import { UploadsTab } from "./UploadsTab";
import { VoicesTab } from "./VoicesTab";

const ROUTE = "/signed-in/teams/$teamId/brands/$brandId/studio/pro/library";
const LIBRARY = "/teams/$teamId/brands/$brandId/pro/library";
/** The Library's own files inside Pro, where a project or a file opens (spec ui-switch, e3). */
const CURRENT = "/teams/$teamId/brands/$brandId/pro/library/files";

/** The prototype's tabs, in its order; the ones with no page here yet are coming soon. */
const TABS = [
  { label: "Projects", tab: undefined },
  { label: "Uploads", tab: "uploads" },
  { label: "Inspiration", tab: null },
  { label: "Ad copy", tab: null },
  { label: "Prompt templates", tab: "prompt-templates" },
  { label: "Voices", tab: "voices" },
] as const;

interface TabProps {
  teamId: string;
  brandId: string;
}

/**
 * The brand's top-level projects, the Library's folders, each opening there. New project names
 * one in a card at the grid's start, as the Library's own Projects does (decision 36).
 */
function Projects({ teamId, brandId }: TabProps) {
  const folders = useListFolders(teamId, brandId);
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const making = useCreateFolder({
    mutation: {
      onSuccess: async () => {
        setCreating(false);
        await queryClient.invalidateQueries({ queryKey: getListFoldersQueryKey(teamId, brandId) });
      },
    },
  });
  const [typed, setTyped] = useState("");
  const [favorites, setFavorites] = useState(false);
  const words = typed.trim().toLowerCase();
  const shown = folders.data?.filter(
    (folder) =>
      folder.parent_id === null &&
      (!favorites || folder.is_favorite) &&
      folder.name.toLowerCase().includes(words),
  );
  // The prototype's "1 subproject", from the same read; its file count is a follow-up (decision 29).
  const subprojects = (id: string) =>
    folders.data?.filter((folder) => folder.parent_id === id).length ?? 0;
  // With no projects read yet, a failed read, or one paused while offline (TanStack Query waits
  // for the network rather than failing), is said alone, with Retry, never under skeletons.
  const unread =
    folders.data !== undefined
      ? null
      : folders.isError
        ? folders.error
        : folders.isPaused
          ? new TransportError("network")
          : null;
  return (
    <>
      <div className={gen.toolbar}>
        <TextInput
          className={classes.search}
          aria-label="Search projects"
          placeholder="Search projects"
          leftSection={<Search size={16} aria-hidden />}
          value={typed}
          onChange={(event) => setTyped(event.currentTarget.value)}
        />
        <button
          type="button"
          className={gen.kind}
          aria-pressed={favorites}
          onClick={() => setFavorites(!favorites)}
        >
          <Star size={14} aria-hidden /> Favorites
        </button>
        <span className={create.grow} />
        <button type="button" className={classes.primary} onClick={() => setCreating(true)}>
          New project
        </button>
      </div>
      {folders.isError && unread === null ? <ErrorAlert error={folders.error} /> : null}
      {making.isError ? <ErrorAlert error={making.error} /> : null}
      {unread !== null ? (
        <EmptyState
          title={toUserMessage(unread)}
          action={
            <Button variant="default" loading={folders.isFetching} onClick={() => void folders.refetch()}>
              Retry
            </Button>
          }
        />
      ) : shown === undefined ? (
        <div className={classes.grid}>
          {[0, 1, 2].map((each) => (
            <Skeleton key={each} className={classes.skeleton} />
          ))}
        </div>
      ) : shown.length === 0 && !creating ? (
        <div className={create.empty}>
          <b>No projects found</b>
          <span className={home.small}>Projects you make in the Library show up here.</span>
        </div>
      ) : (
        <ul className={classes.grid} aria-label="Projects">
          {creating ? (
            <li className={classes.project} data-new>
              <FolderIcon size={20} className={classes.folder} aria-hidden />
              <FolderNameField
                initial=""
                placeholder="New project"
                label="New project"
                pending={making.isPending}
                onSubmit={(name) => making.mutate({ teamId, brandId, data: { name } })}
                onCancel={() => {
                  making.reset();
                  setCreating(false);
                }}
              />
            </li>
          ) : null}
          {shown.map((folder) => {
            const inside = subprojects(folder.id);
            const count = inside === 0 ? null : countOf(inside, "subproject");
            // Its name says all the card shows: the favorite's star and its subprojects too.
            const label = [folder.name, folder.is_favorite ? "favorite" : null, count]
              .filter((each) => each !== null)
              .join(", ");
            return (
              <li key={folder.id}>
                <Link
                  to={CURRENT}
                  params={{ teamId, brandId }}
                  search={{ folder: folder.id }}
                  className={classes.project}
                  aria-label={label}
                  // A name cut by its ellipsis reads whole on hover.
                  title={folder.name}
                >
                  <FolderIcon size={20} className={classes.folder} aria-hidden />
                  <span className={classes.name}>{folder.name}</span>
                  {folder.is_favorite ? <Star size={14} className={classes.star} aria-hidden /> : null}
                  {inside === 0 ? null : (
                    <span className={classes.count} aria-hidden>
                      {inside}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

/**
 * The prototype's `#library`: the Library's projects and uploads, each opening in the current
 * Library through its own links (`?folder=`, `?preview=`); its other tabs come later (spec
 * ui-perf/pro-studio-redesign, PR d3, decision 29).
 */
export function LibraryPage() {
  const { teamId, brandId } = useParams({ from: ROUTE });
  const tab = useSearch({ from: ROUTE }).tab;
  const brand = useListBrands(teamId).data?.find((each) => each.id === brandId);
  return (
    <div className={create.page}>
      <div className={page.head}>
        <div className={page.titles}>
          <h1 className={page.title}>Library</h1>
          <p className={page.lede}>
            Projects, uploads and reusable pieces for {brand?.name ?? "this brand"}.
          </p>
        </div>
        <Link
          to="/teams/$teamId/brands/$brandId/pro/generations"
          params={{ teamId, brandId }}
          className={classes.secondary}
        >
          Generations <ArrowRight size={14} aria-hidden />
        </Link>
      </div>
      <nav className={page.tabs} aria-label="Library tabs">
        {TABS.map((each) =>
          each.tab === null ? (
            <SoonControl key={each.label} className={page.tab}>
              {each.label}
            </SoonControl>
          ) : (
            <Link
              key={each.label}
              to={LIBRARY}
              params={{ teamId, brandId }}
              search={{ tab: each.tab }}
              className={page.tab}
              aria-current={each.tab === tab ? "page" : undefined}
              activeOptions={{ exact: true, includeSearch: true }}
            >
              {each.label}
            </Link>
          ),
        )}
      </nav>
      {/* The brand menu keeps the page mounted: another brand starts each tab anew. */}
      {tab === "uploads" ? (
        <UploadsTab key={`${teamId} ${brandId}`} teamId={teamId} brandId={brandId} />
      ) : tab === "prompt-templates" ? (
        <PromptTemplatesTab key={`${teamId} ${brandId}`} teamId={teamId} brandId={brandId} />
      ) : tab === "voices" ? (
        <VoicesTab key={`${teamId} ${brandId}`} teamId={teamId} brandId={brandId} />
      ) : (
        <Projects key={`${teamId} ${brandId}`} teamId={teamId} brandId={brandId} />
      )}
    </div>
  );
}
